import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBusinessAuth } from '../../contexts/BusinessAuthContext';
import './Customers.css';

const Customers = () => {
  const { token } = useBusinessAuth();
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('lastPurchase');
  const [sortOrder, setSortOrder] = useState('desc');

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:10000';

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/daycare/customers`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch customers');
      }

      const data = await response.json();
      setCustomers(data.customers);
    } catch (err) {
      console.error('Error fetching customers:', err);
      setError('Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (cents) => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleCustomerClick = (customerId) => {
    navigate(`/customers/${customerId}`);
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const filteredAndSortedCustomers = customers
    .filter(customer => {
      const searchLower = searchTerm.toLowerCase();
      return (
        customer.name.toLowerCase().includes(searchLower) ||
        customer.email.toLowerCase().includes(searchLower) ||
        (customer.phone && customer.phone.toLowerCase().includes(searchLower))
      );
    })
    .sort((a, b) => {
      const multiplier = sortOrder === 'asc' ? 1 : -1;

      switch (sortBy) {
        case 'name':
          return multiplier * a.name.localeCompare(b.name);
        case 'totalSpent':
          return multiplier * (a.totalSpent - b.totalSpent);
        case 'totalVideos':
          return multiplier * (a.totalVideos - b.totalVideos);
        case 'creditsRemaining':
          return multiplier * (a.creditsRemaining - b.creditsRemaining);
        case 'lastPurchase':
          return multiplier * (new Date(a.lastPurchase) - new Date(b.lastPurchase));
        case 'joinedDate':
          return multiplier * (new Date(a.joinedDate) - new Date(b.joinedDate));
        default:
          return 0;
      }
    });

  if (loading) {
    return (
      <div className="customers-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading customers...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="customers-container">
        <div className="error-state">
          <p>{error}</p>
          <button onClick={fetchCustomers} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="customers-container">
      <div className="customers-header">
        <div>
          <h1>Customers</h1>
          <p className="customers-count">
            {filteredAndSortedCustomers.length} {filteredAndSortedCustomers.length === 1 ? 'customer' : 'customers'}
          </p>
        </div>
        <button onClick={fetchCustomers} className="refresh-button">
          Refresh
        </button>
      </div>

      <div className="customers-controls">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search by name, email, or phone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
      </div>

      {filteredAndSortedCustomers.length === 0 ? (
        <div className="no-customers">
          <p>
            {searchTerm
              ? 'No customers found matching your search.'
              : 'No customers yet. Customers will appear here once they make their first purchase.'}
          </p>
        </div>
      ) : (
        <div className="customers-table-container">
          <table className="customers-table">
            <thead>
              <tr>
                <th onClick={() => handleSort('name')} className="sortable">
                  Customer
                  {sortBy === 'name' && <span className="sort-indicator">{sortOrder === 'asc' ? '▲' : '▼'}</span>}
                </th>
                <th>Email</th>
                <th>Phone</th>
                <th onClick={() => handleSort('totalSpent')} className="sortable">
                  Total Spent
                  {sortBy === 'totalSpent' && <span className="sort-indicator">{sortOrder === 'asc' ? '▲' : '▼'}</span>}
                </th>
                <th onClick={() => handleSort('totalVideos')} className="sortable">
                  Videos
                  {sortBy === 'totalVideos' && <span className="sort-indicator">{sortOrder === 'asc' ? '▲' : '▼'}</span>}
                </th>
                <th onClick={() => handleSort('creditsRemaining')} className="sortable">
                  Credits Left
                  {sortBy === 'creditsRemaining' && <span className="sort-indicator">{sortOrder === 'asc' ? '▲' : '▼'}</span>}
                </th>
                <th onClick={() => handleSort('lastPurchase')} className="sortable">
                  Last Purchase
                  {sortBy === 'lastPurchase' && <span className="sort-indicator">{sortOrder === 'asc' ? '▲' : '▼'}</span>}
                </th>
                <th onClick={() => handleSort('joinedDate')} className="sortable">
                  Joined
                  {sortBy === 'joinedDate' && <span className="sort-indicator">{sortOrder === 'asc' ? '▲' : '▼'}</span>}
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredAndSortedCustomers.map((customer) => (
                <tr key={customer.id} className="customer-row">
                  <td className="customer-name">{customer.name}</td>
                  <td className="customer-email">{customer.email}</td>
                  <td>{customer.phone || '-'}</td>
                  <td className="amount-cell">{formatCurrency(customer.totalSpent)}</td>
                  <td className="count-cell">{customer.totalVideos}</td>
                  <td className="credits-cell">
                    <span className={`credits-badge ${customer.creditsRemaining === 0 ? 'no-credits' : ''}`}>
                      {customer.creditsRemaining}
                    </span>
                  </td>
                  <td>{formatDate(customer.lastPurchase)}</td>
                  <td>{formatDate(customer.joinedDate)}</td>
                  <td>
                    <button
                      onClick={() => handleCustomerClick(customer.id)}
                      className="view-button"
                    >
                      View Details
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Customers;
